import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { doc, updateDoc } from 'firebase/firestore';
import './StudentView.css';

function StudentView({ students, studentRsvps }) {
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterProgram, setFilterProgram] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [sortDirection, setSortDirection] = useState('asc');
  const [filteredStudents, setFilteredStudents] = useState([]);
  const [isAudioPlaying, setIsAudioPlaying] = useState(null);
  const [expandedGuests, setExpandedGuests] = useState(null);
  const [editingAccomplishments, setEditingAccomplishments] = useState(null);
  const [accomplishmentsText, setAccomplishmentsText] = useState('');
  const [statusMessage, setStatusMessage] = useState(null);
  
  // Local copy of students that we'll modify directly
  const [localStudents, setLocalStudents] = useState([]);
  
  // Initialize local students when props change
  useEffect(() => {
    setLocalStudents([...students]);
  }, [students]);
  
  // Extract unique programs from students
  const extractPrograms = () => {
    const programsSet = new Set();
    
    localStudents.forEach(student => {
      // Add single program field if it exists
      if (student.program) {
        programsSet.add(student.program);
      }
      
      // Add all programs from programs array if it exists
      if (Array.isArray(student.programs)) {
        student.programs.forEach(program => {
          programsSet.add(program);
        });
      }
    });
    
    return [...programsSet].sort();
  };
  
  const uniquePrograms = extractPrograms();
  
  // Combine student data with their RSVP data
  const studentsWithRsvp = localStudents.map(student => {
    const rsvp = studentRsvps.find(r => r.studentId === student.id || r.id === student.id);
    return {
      ...student,
      rsvp: rsvp || null,
      hasConfirmedRsvp: rsvp && (rsvp.status === 'confirmed' || rsvp.completedSteps?.includes('confirmation'))
    };
  });
  
  // Apply filters and sorting
  useEffect(() => {
    let result = [...studentsWithRsvp];
    
    // Apply search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      result = result.filter(student => 
        student.name?.toLowerCase().includes(searchLower) ||
        student.id?.toLowerCase().includes(searchLower) ||
        student.program?.toLowerCase().includes(searchLower) ||
        (Array.isArray(student.programs) && 
         student.programs.some(p => p.toLowerCase().includes(searchLower))) ||
        student.rsvp?.pronunciation?.toLowerCase().includes(searchLower) ||
        student.accomplishments?.toLowerCase().includes(searchLower)
      );
    }
    
    // Apply status filter
    if (filterStatus === 'confirmed') {
      result = result.filter(student => student.hasConfirmedRsvp);
    } else if (filterStatus === 'not-confirmed') {
      result = result.filter(student => !student.hasConfirmedRsvp);
    } else if (filterStatus === 'with-guests') {
      result = result.filter(student => student.rsvp && student.rsvp.guestCount > 0);
    } else if (filterStatus === 'with-pronunciation') {
      result = result.filter(student => 
        student.rsvp && (student.rsvp.pronunciation || student.rsvp.pronunciationAudio)
      );
    } else if (filterStatus === 'with-accomplishments') {
      result = result.filter(student => student.accomplishments && student.accomplishments.trim() !== '');
    }
    
    // Apply program filter
    if (filterProgram !== 'all') {
      result = result.filter(student => {
        // Check in the programs array if it exists
        if (Array.isArray(student.programs)) {
          return student.programs.includes(filterProgram);
        }
        // Fall back to the program field
        return student.program === filterProgram;
      });
    }
    
    // Apply sorting
    result.sort((a, b) => {
      let valueA, valueB;
      
      if (sortBy === 'name') {
        valueA = a.name || '';
        valueB = b.name || '';
      } else if (sortBy === 'id') {
        valueA = a.id || '';
        valueB = b.id || '';
      } else if (sortBy === 'program') {
        valueA = a.program || '';
        valueB = b.program || '';
      } else if (sortBy === 'guestCount') {
        valueA = a.rsvp?.guestCount || 0;
        valueB = b.rsvp?.guestCount || 0;
      } else if (sortBy === 'rsvpDate') {
        valueA = a.rsvp?.updatedAt ? new Date(a.rsvp.updatedAt).getTime() : 0;
        valueB = b.rsvp?.updatedAt ? new Date(b.rsvp.updatedAt).getTime() : 0;
      }
      
      if (sortDirection === 'asc') {
        return valueA > valueB ? 1 : -1;
      } else {
        return valueA < valueB ? 1 : -1;
      }
    });
    
    setFilteredStudents(result);
  }, [studentsWithRsvp, searchTerm, filterStatus, filterProgram, sortBy, sortDirection]);
  
  // Handle sorting change
  const handleSort = (column) => {
    if (sortBy === column) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Default to ascending for new column
      setSortBy(column);
      setSortDirection('asc');
    }
  };
  
  // Format date helper
  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    
    const date = timestamp.toDate 
      ? timestamp.toDate() 
      : new Date(timestamp);
      
    return date.toLocaleString();
  };
  
  // Play/pause audio
  const toggleAudio = (audioUrl, studentId) => {
    if (isAudioPlaying === studentId) {
      // Pause currently playing audio
      document.getElementById(`audio-${studentId}`)?.pause();
      setIsAudioPlaying(null);
    } else {
      // Pause any currently playing audio
      if (isAudioPlaying) {
        document.getElementById(`audio-${isAudioPlaying}`)?.pause();
      }
      
      // Play the new audio
      const audioElement = document.getElementById(`audio-${studentId}`);
      if (audioElement) {
        audioElement.play();
        setIsAudioPlaying(studentId);
        
        // Reset when audio ends
        audioElement.onended = () => setIsAudioPlaying(null);
      }
    }
  };

  // Accomplishments editing functions
  const startEditAccomplishments = (studentId, currentText) => {
    setEditingAccomplishments(studentId);
    setAccomplishmentsText(currentText);
  };

  const cancelEditAccomplishments = () => {
    setEditingAccomplishments(null);
    setAccomplishmentsText('');
  };

  const saveAccomplishments = async (studentId) => {
    try {
      // Update Firestore
      const studentRef = doc(db, "students", studentId);
      await updateDoc(studentRef, {
        accomplishments: accomplishmentsText
      });
      
      // Update local state instead of reloading page
      setLocalStudents(prevStudents => {
        return prevStudents.map(student => {
          if (student.id === studentId) {
            return { ...student, accomplishments: accomplishmentsText };
          }
          return student;
        });
      });
      
      // Exit edit mode
      setEditingAccomplishments(null);
      setAccomplishmentsText('');
      
      // Show success message
      setStatusMessage({ type: 'success', text: 'Accomplishments saved successfully!' });
      setTimeout(() => setStatusMessage(null), 3000);
      
      // REMOVED: window.location.reload() - This was causing the page refresh
    } catch (error) {
      console.error("Error saving accomplishments:", error);
      setStatusMessage({ type: 'error', text: 'Failed to save accomplishments.' });
      setTimeout(() => setStatusMessage(null), 3000);
    }
  };
  
  return (
    <div className="student-view-container">
      <h1>Student RSVPs</h1>
      
      <div className="filters-container">
        <div className="search-box">
          <input
            type="text"
            placeholder="Search students..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="filter-dropdown">
          <label htmlFor="status-filter">Status: </label>
          <select 
            id="status-filter" 
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All Students</option>
            <option value="confirmed">Confirmed RSVPs</option>
            <option value="not-confirmed">No RSVP</option>
            <option value="with-guests">With Guests</option>
            <option value="with-pronunciation">With Pronunciation</option>
            <option value="with-accomplishments">With Accomplishments</option>
          </select>
        </div>
        
        <div className="filter-dropdown">
          <label htmlFor="program-filter">Program: </label>
          <select 
            id="program-filter" 
            value={filterProgram}
            onChange={(e) => setFilterProgram(e.target.value)}
          >
            <option value="all">All Programs</option>
            {uniquePrograms.map(program => (
              <option key={program} value={program}>
                {program}
              </option>
            ))}
          </select>
        </div>
      </div>
      
      <div className="student-count">
        <span>Showing {filteredStudents.length} of {localStudents.length} students</span>
      </div>
      
      {filteredStudents.length > 0 ? (
        <div className="responsive-table-container">
          <table className="students-table">
            <thead>
              <tr>
                <th onClick={() => handleSort('name')} className={sortBy === 'name' ? `sorted-${sortDirection}` : ''}>
                  Student Name
                </th>
                <th onClick={() => handleSort('id')} className={sortBy === 'id' ? `sorted-${sortDirection}` : ''}>
                  Student ID
                </th>
                <th onClick={() => handleSort('program')} className={sortBy === 'program' ? `sorted-${sortDirection}` : ''}>
                  Program
                </th>
                <th>RSVP Status</th>
                <th>Name Pronunciation</th>
                <th onClick={() => handleSort('guestCount')} className={sortBy === 'guestCount' ? `sorted-${sortDirection}` : ''}>
                  Guests
                </th>
                <th onClick={() => handleSort('rsvpDate')} className={sortBy === 'rsvpDate' ? `sorted-${sortDirection}` : ''}>
                  Last Updated
                </th>
                <th>CTE Accomplishments</th>
              </tr>
            </thead>
            <tbody>
              {filteredStudents.map((student) => (
                <tr key={student.id} className={student.hasConfirmedRsvp ? 'confirmed-row' : 'not-confirmed-row'}>
                  <td>{student.name || 'N/A'}</td>
                  <td>{student.id || 'N/A'}</td>
                  <td>
                    {student.programs ? (
                      <div className="programs-list">
                        {student.programs.map((program, index) => (
                          <div key={index} className="program-item">
                            {program}
                          </div>
                        ))}
                      </div>
                    ) : (
                      student.program || 'N/A'
                    )}
                  </td>
                  <td>
                    <span className={`status-badge ${student.hasConfirmedRsvp ? 'confirmed' : 'not-confirmed'}`}>
                      {student.hasConfirmedRsvp ? 'Confirmed' : 'Not Confirmed'}
                    </span>
                  </td>
                  <td>
                    {student.rsvp?.pronunciation && (
                      <div className="pronunciation-text">
                        "{student.rsvp.pronunciation}"
                      </div>
                    )}
                    {student.rsvp?.pronunciationAudio && (
                      <div className="pronunciation-audio">
                        <audio 
                          id={`audio-${student.id}`} 
                          src={student.rsvp.pronunciationAudio} 
                          preload="none"
                        />
                        <button 
                          type="button"
                          onClick={() => toggleAudio(student.rsvp.pronunciationAudio, student.id)}
                          className={isAudioPlaying === student.id ? 'playing' : ''}
                        >
                          {isAudioPlaying === student.id ? '■ Stop' : '▶ Play'}
                        </button>
                      </div>
                    )}
                    {!student.rsvp?.pronunciation && !student.rsvp?.pronunciationAudio && 'Not provided'}
                  </td>
                  <td>
                    {student.rsvp ? (
                      <div className="guest-info">
                        <span className="guest-count">{student.rsvp.guestCount || 0}</span>
                        {student.rsvp.guestCount > 0 && (
                          <button 
                            type="button"
                            className="guest-details-button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              setExpandedGuests(expandedGuests === student.id ? null : student.id);
                            }}
                          >
                            {expandedGuests === student.id ? 'Hide Details' : 'Show Details'}
                          </button>
                        )}
                        {expandedGuests === student.id && student.rsvp.guests && (
                          <div className="guest-details-expanded">
                            {student.rsvp.guests.slice(0, student.rsvp.guestCount).map((guest, index) => (
                              <div key={index} className="guest-item-expanded">
                                <div className="guest-name">{guest.name || 'Not specified'}</div>
                                {guest.relationship && (
                                  <div className="guest-relationship">({guest.relationship})</div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    ) : (
                      'N/A'
                    )}
                  </td>
                  <td>{student.rsvp ? formatDate(student.rsvp.updatedAt) : 'N/A'}</td>
                  <td>
                    <div className="accomplishments-field">
                      {editingAccomplishments === student.id ? (
                        <div className="edit-accomplishments">
                          <textarea
                            value={accomplishmentsText}
                            onChange={(e) => setAccomplishmentsText(e.target.value)}
                            rows={4}
                            placeholder="Certifications, CTSO Membership, Contest Placements, etc."
                          />
                          <div className="edit-buttons">
                            <button 
                              type="button"
                              className="save-button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                saveAccomplishments(student.id);
                              }}
                            >
                              Save
                            </button>
                            <button 
                              type="button"
                              className="cancel-button"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                cancelEditAccomplishments();
                              }}
                            >
                              Cancel
                            </button>
                          </div>
                        </div>
                      ) : (
                        <div className="view-accomplishments">
                          <div className="accomplishments-text">
                            {student.accomplishments || 'None recorded'}
                          </div>
                          <button 
                            type="button"
                            className="edit-button"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              startEditAccomplishments(student.id, student.accomplishments || '');
                            }}
                          >
                            Edit
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="no-results">
          <p>No students match your search criteria.</p>
        </div>
      )}
      
      {statusMessage && (
        <div className={`status-message ${statusMessage.type}`}>
          {statusMessage.text}
        </div>
      )}
    </div>
  );
}

export default StudentView;